import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { generateSchoolSessions } from "../utils/schoolYearUtils";
import { userRequest } from "../utils/requestMethods";
import { useStateContext } from "../contexts/ContextProvider";
import { useSubscription } from "../contexts/SubscriptionContext";

const TermManagementModal = ({ closeModal, school }) => {
  const { refreshSchool } = useStateContext();
  const { refreshSubscription } = useSubscription();
  // Local state for term details. We no longer store _id; we use the term number for matching.
  const [terms, setTerms] = useState([
    { term: "1", label: "1st Term", startDate: "", endDate: "" },
    { term: "2", label: "2nd Term", startDate: "", endDate: "" },
    { term: "3", label: "3rd Term", startDate: "", endDate: "" },
  ]);

  const schoolSessions = generateSchoolSessions(2022, 20);
  const [term, setTerm] = useState("");
  const [session, setSession] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState({ termIndex: null, sessionTerm: false });

  // Pre-fill local state with existing terms from school.settings.terms based on term number
  useEffect(() => {
    if (school?.settings?.terms && school.settings.terms.length > 0) {
      const updatedTerms = terms.map((localTerm) => {
        const existing = school.settings.terms.find(
          (t) => Number(t.term) === Number(localTerm.term)
        );
        if (existing) {
          return {
            ...localTerm,
            startDate: existing.start_date.split("T")[0], // Format date to YYYY-MM-DD
            endDate: existing.end_date.split("T")[0],
          };
        }
        return localTerm;
      });
      setTerms(updatedTerms);
      setTerm(String(school?.settings?.current_term));
      setSession(school?.settings?.current_session);
    }
  }, [school?.settings?.terms]);

  // Check if a term record exists by term number
  const termExists = (termNumber) => {
    return school?.settings?.terms?.some(
      (t) => Number(t.term) === Number(termNumber)
    );
  };

  // Function to handle saving/updating a term
  const handleSave = async (index) => {
    const { term, startDate, endDate, label } = terms[index];
    if (!startDate || !endDate) {
      setError("Please fill in both start and end dates.");
      setTimeout(() => setError(""), 5000);
      return;
    }
    setLoading((prev) => ({ ...prev, termIndex: index }));
    try {
      if (termExists(term)) {
        // If a term record already exists (by term number), update it using PATCH
        await userRequest.patch(`/schools/${school._id}/settings/term`, {
          term: {
            term,
            start_date: startDate,
            end_date: endDate,
          },
        });
        setSuccess(`${label} details updated successfully.`);
      } else {
        // Otherwise, create a new term record with POST
        await userRequest.post(`/schools/${school._id}/settings/term`, {
          term,
          start_date: startDate,
          end_date: endDate,
        });
        setSuccess(`${label} details saved successfully.`);
      }
      refreshSchool();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error(error);
      setError("Failed to save term details. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading((prev) => ({ ...prev, termIndex: null }));
    }
  };

  const handleTermChange = (index, key, value) => {
    const updatedTerms = [...terms];
    updatedTerms[index][key] = value;
    setTerms(updatedTerms);
  };

  // For session-term submission (kept as POST)
  const submitHandler = async (e) => {
    e.preventDefault();
    if (!term || !session) {
      setError("Please select a term and session.");
      setTimeout(() => setError(""), 5000);
      return;
    }
    setLoading((prev) => ({ ...prev, sessionTerm: true }));
    try {
      await userRequest.post(`/schools/${school._id}/settings/session-term`, {
        term: parseInt(term, 10),
        session,
      });
      setSuccess("School term and session added.");
      refreshSchool();
      refreshSubscription();
      setTimeout(() => {
        setSuccess("");
        closeModal();
      }, 2000);
    } catch (error) {
      console.error(error);
      setError("Something went wrong. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading((prev) => ({ ...prev, sessionTerm: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-4xl p-8 overflow-y-auto max-h-[95vh] shadow-2xl">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Term Management</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configure your academic calendar and set the active term.
            </p>
          </div>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-full transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Notifications */}
        {error && (
          <div className="p-4 mb-6 text-sm text-red-800 bg-red-50 rounded-xl border border-red-200 flex items-center" role="alert">
            <svg className="flex-shrink-0 inline w-4 h-4 me-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
            </svg>
            <span className="sr-only">Error</span>
            <div><span className="font-medium">Error!</span> {error}</div>
          </div>
        )}
        {success && (
          <div className="p-4 mb-6 text-sm text-green-800 bg-green-50 rounded-xl border border-green-200 flex items-center" role="alert">
            <svg className="flex-shrink-0 inline w-4 h-4 me-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
            </svg>
            <span className="sr-only">Success</span>
            <div><span className="font-medium">Success!</span> {success}</div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Section 1: Configure Term Dates */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">1</div>
              <h3 className="text-lg font-bold text-gray-800">Configure Term Dates</h3>
            </div>

            <div className="space-y-4">
              {terms.map((termData, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow duration-300">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-bold text-gray-700">{termData.label}</h4>
                    {/* Optional visual indicator if dates are set */}
                    {termData.startDate && termData.endDate && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">Configured</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Start Date</label>
                      <input
                        type="date"
                        value={termData.startDate}
                        onChange={(e) => handleTermChange(index, "startDate", e.target.value)}
                        className="block w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl text-sm py-2 px-3 bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">End Date</label>
                      <input
                        type="date"
                        value={termData.endDate}
                        onChange={(e) => handleTermChange(index, "endDate", e.target.value)}
                        className="block w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl text-sm py-2 px-3 bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleSave(index)}
                    disabled={loading.termIndex === index}
                    className="w-full py-2 bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 font-semibold rounded-xl text-sm transition-all duration-200 flex justify-center items-center gap-2"
                  >
                    {loading.termIndex === index ? (
                      <><span>Saving...</span></>
                    ) : (
                      <><span>Save {termData.label} Dates</span></>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Separator for Desktop */}
          <div className="hidden lg:block w-px bg-gray-200 self-stretch"></div>

          {/* Section 2: Active Session & Term */}
          <div className="flex-1">
            <div className="sticky top-0">
              <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 rounded-full bg-white text-indigo-600 flex items-center justify-center font-bold text-sm shadow-sm">2</div>
                  <h3 className="text-lg font-bold text-indigo-900">Set Active Session</h3>
                </div>

                <p className="text-sm text-indigo-700 mb-6 leading-relaxed">
                  Select the current academic session and term. This will update the dashboard and all school records to reflect the active period.
                </p>

                <div className="space-y-5">
                  <div>
                    <label htmlFor="session" className="block text-sm font-semibold text-indigo-900 mb-2">
                      Academic Session
                    </label>
                    <div className="relative">
                      <select
                        id="session"
                        value={session}
                        onChange={(e) => setSession(e.target.value)}
                        className="block w-full border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl text-base py-3 px-4 bg-white shadow-sm appearance-none"
                      >
                        <option value="">Select Session</option>
                        {schoolSessions.map((sessionItem) => (
                          <option key={sessionItem.value} value={sessionItem.value}>
                            {sessionItem.label}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-indigo-500">
                        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="term" className="block text-sm font-semibold text-indigo-900 mb-2">
                      Current Term
                    </label>
                    <div className="relative">
                      <select
                        id="term"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        className="block w-full border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl text-base py-3 px-4 bg-white shadow-sm appearance-none"
                      >
                        <option value="">Select Term</option>
                        <option value="1">1st Term</option>
                        <option value="2">2nd Term</option>
                        <option value="3">3rd Term</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-indigo-500">
                        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={submitHandler}
                    disabled={loading.sessionTerm || !term || !session}
                    className={`w-full py-4 mt-4 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 ${loading.sessionTerm || !term || !session
                        ? "bg-indigo-300 cursor-not-allowed"
                        : "bg-[#4543B5] hover:bg-indigo-800"
                      }`}
                  >
                    {loading.sessionTerm ? "Activating..." : "Activate Term"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default TermManagementModal;
